import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    TableCell,
    TableRow,
    Typography,
    Stack,
    Switch,
    Button,
    Avatar,
    Chip,
    Box,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Visibility as VisibilityIcon, ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import TableCommon from "../../Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import {
    useUpdateUserMutation,
    useDeleteUserMutation,
    useResendWhatsAppMessageMutation,
} from "../../../store/users/usersApis";
import ActionMenu, { ActionTypes } from "../../Common/ActionMenu";
import ConfirmationDialog from "../../Common/ConfirmationDialog";

const UsersTableComponent = ({
    loading,
    fetching = false,
    count,
    data,
    edit,
    pagination,
    handlePagination,
    onViewSubordinates,
    currentLevel,
    parentUser,
}) => {
    const navigate = useNavigate();
    const [handleUpdate] = useUpdateUserMutation();
    const [handleDelete] = useDeleteUserMutation();
    const [resendWhatsApp] = useResendWhatsAppMessageMutation();
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [userToAction, setUserToAction] = useState(null);
    const [actionType, setActionType] = useState(""); // 'toggle', 'delete', or 'resend-whatsapp'

    const handleToggle = (row) => {
        setUserToAction(row);
        setActionType("toggle");
        setConfirmationOpen(true);
    };

    const handleDeleteUser = (row) => {
        setUserToAction(row);
        setActionType("delete");
        setConfirmationOpen(true);
    };

    const handleResendWhatsApp = (row) => {
        setUserToAction(row);
        setActionType("resend-whatsapp");
        setConfirmationOpen(true);
    };

    const handleConfirmAction = async () => {
        if (actionType === "toggle") {
            handleUpdate({
                id: userToAction?.id,
                name: userToAction?.name,
                email: userToAction?.email,
                mobile: userToAction?.mobile,
                role: userToAction?.role,
                is_active: !userToAction?.is_active,
                is_b2b: userToAction?.is_b2b,
                parent_id: userToAction?.parent_id,
            });
        } else if (actionType === "delete") {
            try {
                const result = await handleDelete(userToAction?.id).unwrap();

                // Check if deletion was blocked due to dependencies
                if (result.canDelete === false) {
                    // Show dependency warning dialog
                    setActionType("delete-with-dependencies");
                    setUserToAction({ ...userToAction, dependencies: result.dependencies });
                    // Don't close the dialog, just change the message
                    return;
                }

                // Deletion was successful
                setConfirmationOpen(false);
            } catch (error) {
                console.error("Error deleting user:", error);
                setConfirmationOpen(false);
            }
        } else if (actionType === "delete-with-dependencies") {
            // For dependency warning, just close the dialog
            // User needs to handle dependencies first
            setConfirmationOpen(false);
        } else if (actionType === "resend-whatsapp") {
            try {
                // For resend WhatsApp, we need to prompt for password
                const password = prompt(`Enter password for ${userToAction?.name} to resend WhatsApp message:`);
                if (password) {
                    await resendWhatsApp({ userId: userToAction?.id, password }).unwrap();
                    alert("WhatsApp message sent successfully!");
                }
                setConfirmationOpen(false);
            } catch (error) {
                console.error("Error resending WhatsApp message:", error);
                alert(`Failed to send WhatsApp message: ${error?.data?.message || error.message}`);
                setConfirmationOpen(false);
            }
        }

        if (actionType !== "delete") {
            setConfirmationOpen(false);
        }
    };

    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
    };

    const handleViewTracking = (row) => {
        navigate(`/tracking?userId=${row.id}`);
    };

    const getRoleColor = (role) => {
        switch (role?.toLowerCase()) {
            case "admin":
                return { bg: "#FFEBEE", color: "#C62828" };
            case "staff":
                return { bg: "#E8F5E8", color: "#2E7D32" };
            case "distributor":
                return { bg: "#F3E5F5", color: "#7B1FA2" };
            default:
                return { bg: "#F5F5F5", color: "#666" };
        }
    };

    // Determine if user can have subordinates
    const canHaveSubordinates = (role) => {
        return role?.toLowerCase() === "distributor";
    };

    let columns = [
        {
            title: "ID",
            field: "id",
            sort: true,
            width: "7%",
            align: "center",
        },
        {
            title: "User",
            field: "name",
            sort: true,
            width: "22%",
            align: "left",
        },
        {
            title: "Contact",
            field: "email",
            sort: false,
            width: "18%",
            align: "left",
        },
        {
            title: "Role",
            field: "role",
            sort: true,
            width: "12%",
            align: "center",
        },
        {
            title: "Status",
            field: "is_active",
            sort: false,
            width: "8%",
            align: "center",
        },
        {
            title: "B2B",
            field: "is_b2b",
            sort: false,
            width: "6%",
            align: "center",
        },
        {
            title: "Created Date",
            field: "created_at",
            sort: true,
            width: "10%",
            align: "center",
        },
        {
            title: "Subordinates",
            field: "subordinates",
            sort: false,
            width: "8%",
            align: "center",
        },
        {
            title: "",
            name: "",
            sort: false,
            width: "9%",
            align: "center",
        },
    ];

    const renderTableData =
        !loading && data && data.length > 0 ? (
            data.map(function (row, index) {
                const roleColors = getRoleColor(row?.role);
                return (
                    <TableRow
                        key={index}
                        sx={{
                            "td, th": {
                                border: 0,
                                padding: "16px 12px",
                                verticalAlign: "middle",
                            },
                            ...(row?.is_active === false && {
                                opacity: 0.6,
                                backgroundColor: "#fafafa",
                            }),
                            "&:hover": {
                                backgroundColor: "#f8f9fa",
                            },
                            transition: "all 0.2s ease-in-out",
                        }}
                    >
                        <TableCell align="center">
                            <Chip
                                label={row.id}
                                size="small"
                                sx={{
                                    backgroundColor: "#e3f2fd",
                                    color: "#1976d2",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    height: 24,
                                }}
                            />
                        </TableCell>
                        <TableCell align="left">
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: "#1976d2",
                                        fontSize: "0.875rem",
                                        fontWeight: 600,
                                    }}
                                >
                                    {row?.name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <Stack spacing={0.5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#2c3e50",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {row?.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {row?.mobile}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </TableCell>
                        <TableCell align="left">
                            <Stack spacing={0.5}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#1976d2",
                                        fontSize: "0.8rem",
                                        fontWeight: 500,
                                    }}
                                >
                                    {row?.email}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: "0.7rem",
                                    }}
                                >
                                    {row?.mobile}
                                </Typography>
                            </Stack>
                        </TableCell>
                        <TableCell align="center">
                            <Chip
                                label={row?.role}
                                size="small"
                                sx={{
                                    backgroundColor: roleColors.bg,
                                    color: roleColors.color,
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    height: 24,
                                    textTransform: "capitalize",
                                }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Chip
                                label={row?.is_active ? "Active" : "Inactive"}
                                size="small"
                                sx={{
                                    backgroundColor: row?.is_active ? "#e8f5e8" : "#ffebee",
                                    color: row?.is_active ? "#2e7d32" : "#d32f2f",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    height: 24,
                                }}
                            />
                        </TableCell>
                        <TableCell align="center">
                            {row?.role === "staff" ? (
                                <Chip
                                    label={row?.is_b2b ? "B2B" : "Regular"}
                                    size="small"
                                    sx={{
                                        backgroundColor: row?.is_b2b ? "#e3f2fd" : "#f5f5f5",
                                        color: row?.is_b2b ? "#1976d2" : "#666",
                                        fontWeight: 600,
                                        fontSize: "0.75rem",
                                        height: 24,
                                    }}
                                />
                            ) : (
                                <Typography variant="caption" sx={{ color: "#ccc", fontSize: "0.7rem" }}>
                                    -
                                </Typography>
                            )}
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                }}
                            >
                                {handleDateTimeDefault(row?.created_at)}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            {canHaveSubordinates(row?.role) ? (
                                <Tooltip title={`View Staff under this ${row?.role}`}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onViewSubordinates && onViewSubordinates(row)}
                                        sx={{
                                            color: "#1976d2",
                                            "&:hover": {
                                                backgroundColor: "#e3f2fd",
                                            },
                                        }}
                                    >
                                        <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Typography variant="caption" sx={{ color: "#ccc", fontSize: "0.7rem" }}>
                                    -
                                </Typography>
                            )}
                        </TableCell>
                        <TableCell align="center">
                            <ActionMenu
                                actions={[
                                    {
                                        ...ActionTypes.VIEW,
                                        onClick: () => edit(row, "View"),
                                    },
                                    {
                                        ...ActionTypes.EDIT,
                                        onClick: () => edit(row, "Edit"),
                                    },
                                    ...(row?.role?.toLowerCase() === "staff"
                                        ? [
                                              {
                                                  ...ActionTypes.TRACKING,
                                                  onClick: () => handleViewTracking(row),
                                              },
                                          ]
                                        : []),
                                    {
                                        ...ActionTypes.WHATSAPP_RESEND,
                                        onClick: () => handleResendWhatsApp(row),
                                    },
                                    {
                                        ...ActionTypes.DELETE,
                                        onClick: () => handleDeleteUser(row),
                                    },
                                ]}
                                row={row}
                                onStatusToggle={handleToggle}
                            />
                        </TableCell>
                    </TableRow>
                );
            })
        ) : (
            <TableRow sx={{ "td, th": { border: 0, padding: "10px" } }}>
                <TableCell colSpan={9} align="center">
                    <Typography variant="body2" sx={{ color: "#666", py: 4 }}>
                        {loading ? `Loading users...` : `No users found`}
                    </Typography>
                </TableCell>
            </TableRow>
        );

    const getConfirmationMessage = () => {
        if (actionType === "toggle") {
            return userToAction?.is_active === false
                ? `Are you sure you want to activate this user?`
                : `Are you sure you want to deactivate this user?`;
        } else if (actionType === "delete") {
            return `Are you sure you want to delete this user? This action cannot be undone.`;
        } else if (actionType === "delete-with-dependencies") {
            const deps = userToAction?.dependencies?.dependentUsers;
            let message = `Cannot delete user "${userToAction?.name}" because they have ${deps?.total} dependent user(s):\n\n`;

            if (deps?.byRole?.staff) {
                message += `• ${deps.byRole.staff} Staff member(s)\n`;
            }
            if (deps?.byRole?.distributor) {
                message += `• ${deps.byRole.distributor} Distributor(s)\n`;
            }

            message += `\nIf you delete this user, all dependent users will lose their reporting structure and data may be lost.\n\nPlease reassign or delete the dependent users first.`;

            return message;
        } else if (actionType === "resend-whatsapp") {
            return `Are you sure you want to resend WhatsApp message with login credentials to ${userToAction?.name}?`;
        }
        return "";
    };

    const getConfirmButtonText = () => {
        if (actionType === "toggle") {
            return userToAction?.is_active === false ? "Yes, Activate" : "Yes, Deactivate";
        } else if (actionType === "delete") {
            return "Yes, Delete";
        } else if (actionType === "delete-with-dependencies") {
            return "OK, I Understand";
        } else if (actionType === "resend-whatsapp") {
            return "Yes, Resend";
        }
        return "Confirm";
    };

    return (
        <>
            <TableCommon
                columns={columns}
                tableData={renderTableData}
                count={count}
                loading={loading || fetching}
                pagination={pagination}
                handlePagination={handlePagination}
                emptyMessage={`No users found`}
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
                open={confirmationOpen}
                onClose={handleCloseConfirmation}
                onConfirm={handleConfirmAction}
                title={getConfirmationMessage()}
                confirmButtonTitle={getConfirmButtonText()}
            />
        </>
    );
};

export default UsersTableComponent;
